import { IconType } from "react-icons";
import {
  SHOW_ADMIN_ANALYTICS_DASHBOARD,
} from "../../constants/adminAccess";
import {
  LuLayoutDashboard,
  LuFileText,
  LuUsers,
  LuLink2,
  LuClipboardList,
  LuPackage,
  LuArrowLeftRight,
  LuCheckCircle2,
  LuClock,
  LuBarChart3,
  LuRefreshCw,
  LuLayers,
  LuUserPlus,
  LuUserCheck,
  LuBriefcase,
  LuHeartHandshake,
  LuWallet,
  LuPieChart,
  LuFileBarChart,
  LuBoxes,
  LuLandmark,
  LuBuilding2,
  LuPiggyBank,
  LuTrendingUp,
  LuUpload,
  LuScrollText,
  LuBanknote,
  LuGem,
  LuFolderOpen,
  LuShoppingCart,
  LuTag,
  LuListOrdered,
  LuInbox,
  LuUserCircle,
  LuSettings,
  LuLogOut,
  LuLibrary,
} from "react-icons/lu";

export type SidebarChild = {
  label: string;
  to: string;
  icon: IconType;
  disabled?: boolean;
  badge?: number;
};

export type SidebarSection = {
  title?: string;
  items: SidebarChild[];
};

export type SidebarNavItem = {
  id: string;
  label: string;
  icon: IconType;
  to?: string;
  children?: SidebarChild[];
  sections?: SidebarSection[];
};

export const isNavPathActive = (pathname: string, to: string) => {
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(`${to}/`);
};

export const collectNavItemPaths = (items: SidebarNavItem[]): string[] => {
  const paths: string[] = [];
  items.forEach((item) => {
    if (item.to) paths.push(item.to);
    item.children?.forEach((c) => paths.push(c.to));
    item.sections?.forEach((s) => s.items.forEach((c) => paths.push(c.to)));
  });
  return paths;
};

export const resolveActiveNavPath = (
  pathname: string,
  paths: string[]
): string | null => {
  let best: string | null = null;
  for (const to of paths) {
    if (!isNavPathActive(pathname, to)) continue;
    if (!best || to.length > best.length) best = to;
  }
  return best;
};

const child = (
  label: string,
  to: string,
  icon: IconType,
  extra?: Partial<SidebarChild>
): SidebarChild => ({ label, to, icon, ...extra });

export const getAdminNavItems = (
  showSuperAdminDashboard: boolean,
  pendingOrdersCount: number
): SidebarNavItem[] => {
  const items: SidebarNavItem[] = [];

  if (showSuperAdminDashboard) {
    items.push({
      id: "ops-dashboard",
      label: "Ops",
      icon: LuLayoutDashboard,
      to: "/super-admin/ops-dashboard",
    });
  }

  items.push({
    id: "curations",
    label: "Curations",
    icon: LuLibrary,
    to: "/curations",
  });

  if (SHOW_ADMIN_ANALYTICS_DASHBOARD) {
    items.push({
      id: "analytics",
      label: "Dashboard",
      icon: LuLayoutDashboard,
      to: "/analytics",
    });
  }

  items.push({
    id: "unlisted",
    label: "Unlisted",
    icon: LuTrendingUp,
    children: [
      child("Transaction Report", "/unlisted/holdings", LuFileText),
      child("Investors", "/unlisted/investors", LuUsers),
      child("DP Mapping", "/unlisted/dpmaping", LuLink2),
      child("Order Requests", "/admin/unlisted/orders", LuClipboardList, {
        badge: pendingOrdersCount,
      }),
      child("Inventory", "/unlisted/inventory", LuPackage),
      child("Admin Transactions", "/unlisted/admin-holdings", LuArrowLeftRight),
      child("Transaction Status", "/unlisted/holdings-status", LuCheckCircle2),
      child("Pending Transactions", "/unlisted/holdings-pending", LuClock),
      child("MIS Report", "/unlisted/mis-report", LuBarChart3),
      child("DP Reconciliation", "/unlisted/reconciliation", LuRefreshCw),
      child("Stock Reconciliation", "/unlisted/stock-reconciliation", LuLayers),
    ],
  });

  items.push({
    id: "investor",
    label: "Investor",
    icon: LuUserPlus,
    children: [
      child("Add Investors", "/investor", LuUserPlus),
      child("Assign RM / Partner", "/assign-rm", LuUserCheck),
    ],
  });

  items.push({
    id: "rm",
    label: "RM",
    icon: LuBriefcase,
    children: [child("RM Details", "/rm", LuBriefcase)],
  });

  items.push({
    id: "partners",
    label: "Partners",
    icon: LuHeartHandshake,
    children: [child("Partners Details", "/partners", LuHeartHandshake)],
  });

  items.push({
    id: "brokerage",
    label: "Payout",
    icon: LuWallet,
    children: [
      child("Partner Payout", "/payout", LuWallet),
      child("Payout Analytics", "/payout-analytics", LuPieChart),
    ],
  });

  items.push({
    id: "reports",
    label: "Reports",
    icon: LuFileBarChart,
    children: [child("Investor Reports", "/investor/reports", LuFileBarChart)],
  });

  items.push({
    id: "products",
    label: "Products",
    icon: LuBoxes,
    children: [
      child("Mutual Funds", "/listing/mf", LuLandmark),
      child("AIF", "/listing/aif", LuBuilding2),
      child("PMS", "/listing/pms", LuBriefcase),
      child("Corporate FD", "/listing/fd", LuPiggyBank),
      child("Unlisted Equity", "/listing/private-stock", LuTrendingUp),
      child("Bonds", "/listing/bonds", LuScrollText),
    ],
  });

  items.push({
    id: "upload",
    label: "Upload",
    icon: LuUpload,
    children: [
      child("AIF", "/upload/file-aif", LuBuilding2),
      child("PMS", "/upload/file-pms", LuBriefcase),
      child("Bonds", "/upload/file-bonds", LuScrollText),
      child("Fixed Deposits", "/upload/file-bonds", LuBanknote, {
        disabled: true,
      }),
      child("Private Equity", "/upload/file-bonds", LuGem, { disabled: true }),
    ],
  });

  return items;
};

export const getDefaultNavItems = (
  userType: string | null,
  pendingOrdersCount: number
): SidebarNavItem[] => {
  const items: SidebarNavItem[] = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: LuLayoutDashboard,
      to: "/dashboard",
    },
  ];

  if (userType === "2") {
    items.push({
      id: "unlisted",
      label: "Unlisted",
      icon: LuTrendingUp,
      children: [
        child("Transaction Report", "/unlisted/holdings", LuFileText),
        child("Investors", "/unlisted/investors", LuUsers),
        child("Orders", "/rm/unlisted/orders", LuListOrdered, {
          badge: pendingOrdersCount,
        }),
        child("RM Transactions", "/unlisted/admin-holdings", LuArrowLeftRight),
        child("Transaction Status", "/unlisted/holdings-status", LuCheckCircle2),
        child("Pending Transactions", "/unlisted/holdings-pending", LuClock),
        child("MIS Report", "/unlisted/mis-report", LuBarChart3),
      ],
    });
  }

  items.push({
    id: "investor",
    label: "Investor",
    icon: LuUsers,
    children: [child("Add Investors", "/investor", LuUserPlus)],
  });

  items.push({
    id: "requests",
    label: "Requests",
    icon: LuInbox,
    sections: [
      {
        title: "Orders & Requests",
        items: [
          child("All Orders & Requests", "/requests/all-orders", LuInbox),
          child("Profile Updates", "/requests", LuUserCircle),
        ],
      },
      /* Kept for future/reference:
      {
        title: "AIF",
        items: [
          child("Buy", "/aif/buy", LuShoppingCart),
          child("Sell", "/aif/sell", LuTag),
        ],
      },
      {
        title: "PMS",
        items: [
          child("Buy", "/pms/buy", LuShoppingCart),
          child("Sell", "/pms/sell", LuTag),
        ],
      },
      */
    ],
  });

  items.push({
    id: "reports",
    label: "Reports",
    icon: LuFileBarChart,
    children: [child("Investor Reports", "/investor/reports", LuFileBarChart)],
  });

  items.push({
    id: "products",
    label: "Products",
    icon: LuBoxes,
    children: [
      child("Mutual Funds", "/listing/mf", LuLandmark),
      child("AIF", "/listing/aif", LuBuilding2),
      child("PMS", "/listing/pms", LuBriefcase),
      child("Corporate FD", "/listing/fd", LuPiggyBank),
      child("Unlisted Equity", "/listing/private-stock", LuTrendingUp),
      child("Bonds", "/listing/bonds", LuScrollText),
    ],
  });

  if (userType === "6") {
    items.push({
      id: "brokerage",
      label: "Payout",
      icon: LuWallet,
      children: [child("Partner Payout", "/payout", LuWallet)],
    });
  }

  items.push({
    id: "holdings",
    label: "Holdings",
    icon: LuFolderOpen,
    children: [
      child("Investors", "/holdings", LuUsers),
      // child("Upload", "/uploadHoldings", LuUpload, { disabled: true }),
    ],
  });

  return items;
};

export const bottomNavItems = {
  settings: {
    label: "Settings",
    icon: LuSettings,
    to: "/profile",
  },
  logout: {
    label: "Logout",
    icon: LuLogOut,
  },
};
